import React, {type ChangeEvent, type FormEvent, useEffect, useRef, useState} from "react";
import KeyPairTable, {type KeyDataRef} from "../components/data_table/KeyPairTable.tsx";
import {Button, Form, Modal} from "react-bootstrap";
import {swal_util} from "../lib/SwalUtil.ts";
import {keypair, KeypairAPIConnectorError} from "../lib/Keypair.ts";
import {Navbar} from "../components/Navbar.tsx";
import {useTranslation} from "react-i18next";

interface ContentProps {
    toggleSidebar: () => void;
}

const KeyPair = ({toggleSidebar}: ContentProps) => {
    const {t} = useTranslation();

    const fetchCalledRef = useRef(false);
    const dataTableRef = useRef<KeyDataRef>(null);
    const [createKeyModalOpen, setCreateKeyModalOpen] = useState(false);
    const [getKeyModalOpen, setGetKeyModalOpen] = useState(false);
    const [data, setData] = useState<{
        name: string;
        defaultData: string;
        password: string;
        private: File | null;
        public: File | null;
    }>({
        name: "",
        defaultData: "0",
        password: "",
        private: null,
        public: null,
    });

    useEffect(() => {
        if (fetchCalledRef.current) return;
        fetchCalledRef.current = true;
        dataTableRef.current?.redraw();
    }, [fetchCalledRef, dataTableRef]);

    function openCreateKeyModal() {
        setCreateKeyModalOpen(true)
    }

    function openGetKeyModal() {
        setGetKeyModalOpen(true);
    }

    function closeCreateKeyModal() {
        setCreateKeyModalOpen(false);
    }

    function closeUploadKeyModal() {
        setGetKeyModalOpen(false);
    }

    async function onClickGenerate(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (data.name === "") {
            return swal_util.show_warning(t("keypair.placeholderName"));
        }
        try {
            swal_util.show_loading(t("keypair.creating"));
            const res = await keypair.generate(data.name, data.defaultData === "1", data.password);
            res.download();
            dataTableRef.current!.redraw();
            closeCreateKeyModal();
            setData((prev) => ({...prev, name: "", defaultData: "0", password: ""}));

            await swal_util.hide_loading();
            await swal_util.show_success(t("keypair.created"));
        } catch (err) {
            await swal_util.hide_loading();
            if (err instanceof KeypairAPIConnectorError) {
                await swal_util.show_error(`${err.localization}`);
            } else {
                await swal_util.show_error(`${t("keypair.errorKeyCreate")} ${t("common.adminContact")}`);
            }
            console.error(err);
        }
    }

    async function onClickUpload(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (data.name === "") {
            return swal_util.show_warning(t("keypair.placeholderName"));
        }
        if (data.private !== null && data.public !== null) {
            try {
                await keypair.upload(data.name, data.defaultData === "1", data.private, data.public, data.password);
                dataTableRef.current!.redraw();
                closeUploadKeyModal();
                setData((prev) => ({...prev, name: "", defaultData: "0", password: "", public: null, private: null}));
            } catch (err) {
                if (err instanceof KeypairAPIConnectorError) {
                    await swal_util.show_error(`${err.localization}`);
                } else {
                    await swal_util.show_error(t("keypair.errorKeyUpload"));
                }
                console.error(err);
            }
        } else await swal_util.show_warning(t("keypair.selectKeyRequired"));

    }

    const onChangePrivateKey = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            setData((prev) => ({...prev, private: files[0]}));
        }
    };

    const onChangePublicKey = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            setData((prev) => ({...prev, public: files[0]}));
        }
    };

    async function onClickKeyPairDelete() {
        const selectedRows = dataTableRef.current?.getSelectedData() || [];
        if (selectedRows.length === 0) {
            await swal_util.show_warning(t("keypair.noKeySelected"));
            return;
        }

        const result = await swal_util.show_question(
            t("keypair.deleteKeyPair"),
            t("keypair.deleteConfirm")
        );
        if (!result.isConfirmed) return;

        try {
            swal_util.show_loading(t("keypair.deleting"));
            for (const row of selectedRows) {
                try {
                    await keypair.delete(row.name);
                } catch (err) {
                    try { await swal_util.hide_loading(); } catch {}
                    if (err instanceof KeypairAPIConnectorError) {
                        const text = err.localization.replace("{name}", row.name);
                        await swal_util.show_warning(text);
                    } else {
                        await swal_util.show_error(
                            `${t("keypair.errorKeyDelete")} ${t("common.adminContact")}`
                        );
                    }
                    console.error(err);
                    return;
                }
            }

            await dataTableRef.current?.redraw();
            await swal_util.hide_loading();
            await swal_util.show_success(t("keypair.deleted"));
        } catch (err) {
            try { await swal_util.hide_loading(); } catch {}
            if (err instanceof KeypairAPIConnectorError) {
                const text = err.localization.replace("{name}", "");
                await swal_util.show_warning(text);
            } else {
                await swal_util.show_error(
                    `${t("keypair.errorKeyDelete")} ${t("common.adminContact")}`
                );
            }
            console.error(err);
        }
    }

    return (
        <main className="d-block col ps-2 pt-3 min-vh-100">
            <Navbar toggleSidebar={toggleSidebar}/>
            <article className="blueshift_main" id="wrapper">
                <div className="p-4 pt-2">
                    <div className="row">
                        <h6 className="text-primary pt-2 col-6">{t("title.keyManagement")}</h6>
                        <div className="text-end col-6">
                            <button type="submit" className="button rounded-3 showmodal py-2"
                                    onClick={openCreateKeyModal}>
                                {t("common.create")}
                            </button>
                            <button type="button" className="button rounded-3 showmodal py-2 mx-1"
                                    onClick={openGetKeyModal}>
                                {t("keypair.importKey")}
                            </button>
                            <button type="button" className="button_red rounded-3 showmodal py-2"
                                    style={{fontSize: "14px"}} onClick={onClickKeyPairDelete}>
                                {t("common.delete")}
                            </button>
                        </div>
                    </div>
                    <div className="text-center align-items-center">
                        <KeyPairTable ref={dataTableRef}/>
                    </div>
                </div>
            </article>
            <Modal show={createKeyModalOpen} onHide={closeCreateKeyModal} className="modal-lg">
                <Form onSubmit={onClickGenerate}>
                    <Modal.Header>
                        <Modal.Title><h6 className="text-primary pt-2">{t("keypair.createKeyPair")}</h6></Modal.Title>
                        <Button variant="white" className="btn_close" onClick={closeCreateKeyModal}>X
                        </Button>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row">
                            <div className="col-3">
                                <label htmlFor="key_name" className="mt-1">{t("keypair.name")}</label>
                            </div>
                            <div className="col-9">
                                <input
                                    className="form-control"
                                    id="key_name"
                                    type="text" value={data.name}
                                    onChange={(e) => setData(prev => ({...prev, name: e.target.value}))}
                                    placeholder={t("keypair.placeholderName")}
                                />
                            </div>

                        </div>
                        <div className="row mt-4">
                            <div className="col-3">
                                <label htmlFor="public-key" className="mt-1">{t("keypair.publickey")}</label>
                            </div>

                            <div className="col-9">
                                <select id="public-key" className="form-control" value={data.defaultData}
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setData(prev => ({
                                            ...prev,
                                            defaultData: e.target.value
                                        }))}>
                                    <option value="0">{t("common.no")}</option>
                                    <option value="1">{t("common.yes")}</option>
                                </select>
                            </div>
                        </div>


                        <div className="row mt-4">
                            <div className="col-3">
                                <label htmlFor="password" className="mt-1">{t("common.password")}</label>
                            </div>
                            <div className="col-9">
                                <input
                                    id="password"
                                    type="password"
                                    className="form-control"
                                    value={data.password}
                                    onChange={(e) => setData(prev => ({...prev, password: e.target.value}))}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button type="submit" variant="primary">
                            {t("keypair.createKeyPair")}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={getKeyModalOpen} onHide={closeUploadKeyModal} className="modal-lg">
                <Form onSubmit={onClickUpload}>
                    <Modal.Header>
                        <Modal.Title><h6 className="text-primary pt-2">{t("keypair.keyUpload")}</h6></Modal.Title>
                        <Button variant="white" className="btn_close" onClick={closeUploadKeyModal}>X
                        </Button>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="form-group mb-4">
                            <h6 className="mb-2">{t("keypair.name")}</h6>
                            <input className="form-control"
                                   placeholder={t("keypair.placeholderName")} value={data.name}
                                   onChange={(e) => setData(prev => ({...prev, name: e.target.value}))}/>
                        </div>
                        <div className="form-group mb-4">
                            <h6 className="mb-2">{t("keypair.primarykey")}</h6>
                            <select id="upload_key_default" name="key_default"
                                    className="form-control" value={data.defaultData}
                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setData(prev => ({
                                        ...prev,
                                        defaultData: e.target.value
                                    }))}>
                                <option value="0">{t("common.no")}</option>
                                <option value="1">{t("common.yes")}</option>
                            </select>
                        </div>
                        <div className="form-group mb-4">
                            <h6 className="mb-2">{t("common.password")}</h6>
                            <input className="form-control" type="password" value={data.password}
                                   onChange={(e) => setData(prev => ({...prev, password: e.target.value}))}/>
                            <br/>
                            <span style={{color: "#842029"}}>
                                {t("keypair.passwordInfo1")}<br/>
                                {t("keypair.passwordInfo2")}
                            </span>
                        </div>

                        <div className="form-group mb-4">
                            <h6 className="mt-1 mb-2">{t("keypair.privatekey")}</h6>
                            <input type="file"
                                   onChange={onChangePrivateKey}/>
                        </div>

                        <div className="form-group mb-4">
                            <h6 className="mt-1 mb-2">{t("keypair.publickey")}</h6>
                            <input type="file"
                                   onChange={onChangePublicKey}/>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button type="submit" variant="primary">
                            {t("common.upload")}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </main>
    );
}

export default KeyPair;